import { Box, Checkbox, FormControlLabel, Switch, Typography } from "@mui/material";
import {
  applyGrantToggle,
  applyPackageNames,
  areaFlagNames,
  areaIncluded,
  areaIsOn,
  namesAreOn,
  optionAllowed,
  permissionAreas,
} from "../constants/users";

export default function PermissionAreas({
  mode = "grant",
  permissions,
  companyPermissions,
  onChange,
  disabled = false,
  columns = 1,
  showSuperUser = false,
  isSuperUser = false,
  onSuperUserChange,
  superUserDisabled = false,
}) {
  const includedCount = permissionAreas.filter((area) =>
    areaIsOn(permissions, area),
  ).length;

  const setPackage = (names, turnOn) => {
    if (disabled) return;
    onChange(applyPackageNames(permissions, names, turnOn));
  };

  const setGrant = (area, optionName) => {
    if (disabled) return;
    onChange(
      applyGrantToggle(permissions, area, optionName, companyPermissions),
    );
  };

  return (
    <Box>
      {showSuperUser && (
        <Box sx={{ mb: 1.5 }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                checked={Boolean(isSuperUser)}
                onChange={() => onSuperUserChange?.(!isSuperUser)}
                disabled={superUserDisabled || disabled}
              />
            }
            label="Super user"
          />
          {isSuperUser && (
            <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
              Super users have every feature this company has paid for.
            </Typography>
          )}
        </Box>
      )}

      {!isSuperUser && mode === "package" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {includedCount} of {permissionAreas.length} areas included
        </Typography>
      )}

      {!isSuperUser && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              columns > 1 ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
            gap: 1.5,
          }}
        >
          {permissionAreas.map((area) => (
            <AreaCard
              key={area.id}
              area={area}
              mode={mode}
              permissions={permissions}
              companyPermissions={companyPermissions}
              disabled={disabled}
              columns={columns}
              onPackage={setPackage}
              onGrant={setGrant}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function AreaCard({
  area,
  mode,
  permissions,
  companyPermissions,
  disabled,
  columns,
  onPackage,
  onGrant,
}) {
  const included = areaIncluded(companyPermissions, area);
  const spanReporting = area.id === "reporting" && columns > 1;

  if (mode === "grant" && !included) {
    return (
      <Box
        sx={{
          ...cardSx,
          gridColumn: spanReporting ? "1 / -1" : "auto",
          bgcolor: "#F8F8F8",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {area.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Not included in your subscription
        </Typography>
      </Box>
    );
  }

  if (mode === "package") {
    const names = areaFlagNames(area);
    const on = namesAreOn(permissions, names);
    return (
      <Box sx={{ ...cardSx, gridColumn: spanReporting ? "1 / -1" : "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {area.label}
          </Typography>
          <Switch
            checked={on}
            onChange={(event) => onPackage(names, event.target.checked)}
            disabled={disabled}
            inputProps={{ "aria-label": area.label }}
          />
        </Box>
        {area.packageGroups && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {area.packageGroups.map((group) => {
              const groupOn = namesAreOn(permissions, group.names);
              return (
                <FormControlLabel
                  key={group.id}
                  sx={{ ml: 0, mr: 1 }}
                  control={
                    <Switch
                      size="small"
                      checked={groupOn}
                      onChange={(event) =>
                        onPackage(group.names, event.target.checked)
                      }
                      disabled={disabled}
                      inputProps={{ "aria-label": group.label }}
                    />
                  }
                  label={
                    <Typography variant="body2">{group.label}</Typography>
                  }
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  const options = area.options.filter((option) =>
    optionAllowed(companyPermissions, option.name),
  );
  const visibleNames = new Set(options.map((option) => option.name));

  return (
    <Box sx={{ ...cardSx, gridColumn: spanReporting ? "1 / -1" : "auto" }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {area.label}
      </Typography>
      {options.map((option) => {
        const nested = Boolean(
          option.requires && visibleNames.has(option.requires),
        );
        return (
          <FormControlLabel
            key={option.name}
            sx={{
              ml: nested ? 2 : 0,
              display: "flex",
              alignItems: "center",
            }}
            control={
              <Checkbox
                size="small"
                checked={permissions?.[option.name] === true}
                onChange={() => onGrant(area, option.name)}
                disabled={disabled}
              />
            }
            label={<Typography variant="body2">{option.label}</Typography>}
          />
        );
      })}
    </Box>
  );
}

const cardSx = {
  border: "1px solid #E6E6E6",
  borderRadius: 1,
  px: 1.5,
  py: 1,
};
